import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import { renderError } from "../../utils/ModuleFunctions";
import { useDropzone } from "react-dropzone";
import AxiosInstance from "../../utils/AxiosInstance";
import * as Yup from "yup";
import { Form as MyForm } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
	MdInfoOutline,
	MdContactPhone,
	MdBed,
	MdPhotoLibrary,
	MdCheck,
	MdArrowBack,
	MdArrowForward,
	MdSave,
	MdCloudUpload,
	MdClose,
	MdErrorOutline,
	MdDirectionsWalk,
	MdDirectionsCar,
} from "react-icons/md";

const API = process.env.REACT_APP_API_URL;

// label + input + hint + error, laid out on the form grid
function FormField({ label, name, hint, required, span, children }) {
	return (
		<div className={`ad-form-field${span ? ` ad-span-${span}` : ""}`}>
			<label className="ad-form-label" htmlFor={name}>
				{label}
				{required && <span className="ad-required">*</span>}
			</label>
			{children || <Field id={name} name={name} className="ad-input" />}
			{hint && <span className="ad-hint">{hint}</span>}
			<ErrorMessage name={name} render={renderError} />
		</div>
	);
}

function SectionTitle({ children, text }) {
	return (
		<div className="ad-form-section">
			<h4>{children}</h4>
			{text && <p>{text}</p>}
		</div>
	);
}

export default function EditResidence(props) {
	const navigate = useNavigate();
	const { slug } = useParams();
	const [registered, setRegistered] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [coverImage, setCoverImage] = useState();
	const [coverPreview, setCoverPreview] = useState();
	const [accepted, setAccepted] = useState([]);
	const [previews, setPreviews] = useState([]);
	const [locations, setLocations] = useState([]);
	const [residence, setResidence] = useState();
	const [facilityArr, setFacilityArr] = useState([]);
	const [facilityChecked, setFacilityChecked] = useState([]);

	const onDrop = useCallback((acceptedFiles) => {
		setAccepted(acceptedFiles);
	}, []);

	useEffect(() => {
		let cancelled = false;
		const fetchAll = async () => {
			setIsLoading(true);
			setLoadError(false);
			try {
				const [resRes, locRes, facRes] = await Promise.all([
					AxiosInstance({ method: "get", url: `/api/v1/residences/${slug}` }),
					AxiosInstance({ method: "get", url: `/api/v1/locations` }),
					AxiosInstance({ method: "get", url: `/api/v1/facilities` }),
				]);
				if (cancelled) return;
				setResidence(resRes.data.data);
				setRegistered(!!resRes.data.data?.registered);
				setFacilityChecked(resRes.data.facilities || []);
				setLocations(locRes.data.data || []);
				setFacilityArr(facRes.data.data || []);
			} catch (err) {
				if (cancelled) return;
				console.log(err);
				setLoadError(true);
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchAll();
		return () => {
			cancelled = true;
		};
	}, [slug]);

	// previews for newly picked images
	useEffect(() => {
		const urls = accepted.map((file) => URL.createObjectURL(file));
		setPreviews(urls);
		return () => urls.forEach((u) => URL.revokeObjectURL(u));
	}, [accepted]);
	useEffect(() => {
		if (!coverImage) return setCoverPreview(undefined);
		const url = URL.createObjectURL(coverImage);
		setCoverPreview(url);
		return () => URL.revokeObjectURL(url);
	}, [coverImage]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: "image/*",
	});

	const initialValues = useMemo(() => {
		if (!residence) return null;
		const r = residence;
		return {
			name: r.name ?? "",
			residenceType: r.residenceType ?? "",
			location: r.location?._id ?? "",
			distance: r.distance ?? "",
			distanceType: r.distanceType ?? "",
			lng: r.gpsAddress?.coordinates?.[0] ?? "",
			lat: r.gpsAddress?.coordinates?.[1] ?? "",
			digitalAddress: r.digitalAddress ?? "",
			bookingLink: r.bookingLink ?? "",
			managersName: r.managersName ?? "",
			managersContact: r.managersContact ?? "",
			portersName: r.portersName ?? "",
			portersContact: r.portersContact ?? "",
			ownersName: r.ownersName ?? "",
			ownersContact: r.ownersContact ?? "",
			roomsTotal: r.roomsTotal ?? "",
			totalBedspaces: r.totalBedspaces ?? "",
			maleCapacity: r.maleCapacity ?? "",
			femaleCapacity: r.femaleCapacity ?? "",
			registered: r.registered ?? false,
			twoInOne: r.actualPrices?.twoInOne ?? "",
			threeInOne: r.actualPrices?.threeInOne ?? "",
			fourInOne: r.actualPrices?.fourInOne ?? "",
			others: r.actualPrices?.others ?? "",
			bedSpaceStatus: r.bedSpaceStatus ?? "",
			description: r.description ?? "",
			images: [],
			// one entry per facility; existing ones start ticked with their count
			facilities: facilityArr.map((f) => {
				const existing = facilityChecked.find((el) => el?.facility?._id === f._id);
				return existing ? { id: [f._id], num: existing.count ?? "" } : { id: "", num: "" };
			}),
			coverImage: "",
		};
	}, [residence, facilityArr, facilityChecked]);

	const handleSubmit = async (values, setSubmit) => {
		let formData = new FormData();
		const facilities_str = JSON.stringify(values.facilities);

		formData.append("name", values.name);
		formData.append("residenceType", values.residenceType);
		formData.append("location", values.location);
		formData.append("distance", values.distance);
		formData.append("distanceType", values.distanceType);
		formData.append("digitalAddress", values.digitalAddress);
		formData.append("bookingLink", values.bookingLink);
		formData.append("description", values.description);

		formData.append("managersName", values.managersName);
		formData.append("managersContact", values.managersContact);
		formData.append("portersName", values.portersName);
		formData.append("portersContact", values.portersContact);
		formData.append("ownersName", values.ownersName);
		formData.append("ownersContact", values.ownersContact);

		formData.append("roomsTotal", values.roomsTotal);
		formData.append("totalBedspaces", values.totalBedspaces);
		formData.append("maleCapacity", values.maleCapacity);
		formData.append("femaleCapacity", values.femaleCapacity);
		formData.append("facilities", facilities_str);
		formData.append("twoInOne", values.twoInOne);
		formData.append("threeInOne", values.threeInOne);
		formData.append("fourInOne", values.fourInOne);
		formData.append("others", values.others);
		formData.append("bedSpaceStatus", values.bedSpaceStatus);
		formData.append("registered", registered);
		formData.append("lng", values.lng);
		formData.append("lat", values.lat);
		if (coverImage) {
			formData.append("coverImage", coverImage);
		}
		accepted.forEach((el) => {
			formData.append("images", el);
		});

		try {
			await AxiosInstance({
				method: "post",
				url: `/api/v1/residences/${residence?._id}`,
				headers: {
					accept: "application/json",
				},
				data: formData,
			});
			setSubmit(false);
			navigate("/admin/residences");
		} catch (err) {
			setSubmit(false);
			const message = err?.response?.data?.message;
			console.log("logged Error", message || err);
			if (message?.startsWith("Can't extract")) {
				toast.error(
					"Please provide a correct coordinates for the Longitude and the Latitude",
					{ position: "top-center" }
				);
			} else {
				toast.error(message || "Couldn't save the changes. Please try again.", {
					position: "top-center",
				});
			}
		}
	};

	if (isLoading) return <div id="dash-loader" />;

	if (loadError || !initialValues) {
		return (
			<div className="ad-card ad-empty">
				<MdErrorOutline className="ad-empty-icon" />
				<h3>Couldn&apos;t load this residence</h3>
				<button className="ad-btn ad-btn--primary" onClick={() => navigate(-1)}>
					Go back
				</button>
			</div>
		);
	}

	const existingImages = [residence.coverImage, ...(residence.images || [])].filter(Boolean);

	return (
		<div className="ad-page">
			<ToastContainer className="top-margin" />
			<section className="ad-card ad-head">
				<div className="ad-head-text">
					<span className="ad-eyebrow">Edit Residence</span>
					<h2 className="ad-title">{residence.name}</h2>
					<p className="ad-muted">
						Update the details below. Fields marked <span className="ad-required">*</span> are
						required.
					</p>
				</div>
			</section>

			<FormStepper
				initialValues={initialValues}
				onSubmit={async (values, setSubmit) => {
					await handleSubmit(values, setSubmit);
				}}
			>
				{/* step 1 */}
				<FormikStep
					label="Basic Info"
					icon={<MdInfoOutline />}
					validationSchema={Yup.object({
						name: Yup.string("must be a string")
							.min(5, "Name is too short must be 5 characters and above")
							.required("Residence is Required"),
						residenceType: Yup.string()
							.min(2, "Required")
							.required("Residence Type is required"),
						location: Yup.string().min(2, "Required Field").required("Location is Required"),
						digitalAddress: Yup.string().nullable(),
						bookingLink: Yup.string()
							.matches(
								/((http?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
								"Please Enter a correct url!"
							)
							.nullable(),
					})}
				>
					<SectionTitle text="Name, type and where the residence is located.">
						Residence
					</SectionTitle>
					<div className="ad-form-grid">
						<FormField label="Residence Name" name="name" required hint="Example: Nana Adoma" />
						<FormField label="Type of Residence" name="residenceType" required>
							<Field as="select" id="residenceType" name="residenceType" className="ad-input">
								<option value="Hostel">Hostel</option>
								<option value="Homestel">Homestel</option>
								<option value="Obuasi_Campus">Obuasi Campus</option>
								<option value="Other">Other</option>
							</Field>
						</FormField>
						<FormField label="Residence Location" name="location" required>
							<Field as="select" id="location" name="location" className="ad-input">
								<option value="">Select location</option>
								{locations.map((item) => (
									<option key={item._id} value={item._id}>
										{item.name}
									</option>
								))}
							</Field>
						</FormField>
					</div>

					<SectionTitle text="Used for the map, directions and booking.">
						Address &amp; Links
					</SectionTitle>
					<div className="ad-form-grid">
						<FormField
							label="GhanaPost GPS Address"
							name="digitalAddress"
							hint="Example: AK-1310-3223, use GhanaPost"
						/>
						<FormField label="Latitude" name="lat" hint="Between -90 and 90">
							<Field id="lat" type="number" step="any" name="lat" className="ad-input" placeholder="6.6848275" />
						</FormField>
						<FormField label="Longitude" name="lng" hint="Between -180 and 180">
							<Field id="lng" type="number" step="any" name="lng" className="ad-input" placeholder="-1.5723632" />
						</FormField>
						<FormField
							label="Booking Link URL"
							name="bookingLink"
							span={2}
							hint="Eg: https://saintpeters.studentroombook.com"
						>
							<Field id="bookingLink" type="url" name="bookingLink" className="ad-input" placeholder="https://" />
						</FormField>
						<FormField label="Distance" name="distance" hint="Eg. 14 min">
							<Field id="distance" name="distance" className="ad-input" placeholder="14 min" />
						</FormField>
					</div>

					<div className="ad-form-grid">
						<div className="ad-form-field" role="group" aria-labelledby="distanceType-label">
							<span id="distanceType-label" className="ad-form-label">
								Distance Type
							</span>
							<div className="ad-segment">
								<label>
									<Field type="radio" name="distanceType" value="0" />
									<span>
										<MdDirectionsWalk /> Walking
									</span>
								</label>
								<label>
									<Field type="radio" name="distanceType" value="1" />
									<span>
										<MdDirectionsCar /> Driving
									</span>
								</label>
							</div>
							<ErrorMessage name="distanceType" render={renderError} />
						</div>
						<FormField label="Description" name="description" span={2}>
							<Field
								id="description"
								as="textarea"
								rows={4}
								name="description"
								className="ad-input ad-textarea"
								placeholder="Short description of the Residence"
							/>
						</FormField>
					</div>
				</FormikStep>

				{/* step 2 */}
				<FormikStep
					label="Contacts & Pricing"
					icon={<MdContactPhone />}
					validationSchema={Yup.object({
						ownersName: Yup.string("Must be a String").min(5, "Should be 5 letters and above").nullable(),
						ownersContact: Yup.string("Must be a String").min(10, "Should be telephone number").nullable(),
						managersName: Yup.string("Must be a String").min(5, "Should be 5 letters and above").nullable(),
						managersContact: Yup.string("Must be a String").min(10, "Should be telephone number").nullable(),
						portersName: Yup.string("Must be a String").min(5, "Should be 5 letters and above").nullable(),
						portersContact: Yup.string("Must be a String").min(10, "Should be telephone number").nullable(),
					})}
				>
					<SectionTitle text="People students can reach about this residence.">Contacts</SectionTitle>
					<div className="ad-form-grid ad-form-grid--2">
						<FormField label="Owner's Name" name="ownersName" hint="Example: Kate Williams" />
						<FormField label="Owner's Contact" name="ownersContact" hint="Example: 0201658894">
							<Field id="ownersContact" type="tel" name="ownersContact" className="ad-input" />
						</FormField>
						<FormField label="Manager's Name" name="managersName" hint="Example: Noble Akoh" />
						<FormField label="Manager's Contact" name="managersContact" hint="Eg: 0201289778/0552311893">
							<Field id="managersContact" type="tel" name="managersContact" className="ad-input" />
						</FormField>
						<FormField label="Porter's Name" name="portersName" hint="Example: Francis Dogbe" />
						<FormField label="Porter's Contact" name="portersContact" hint="Example: 0201658894">
							<Field id="portersContact" type="tel" name="portersContact" className="ad-input" />
						</FormField>
					</div>

					<SectionTitle text="Amounts in GH₵ per bed space.">Price Charges</SectionTitle>
					<div className="ad-form-grid ad-form-grid--4">
						<FormField label="Two - In - One" name="twoInOne" hint="Example: 4,500" />
						<FormField label="Three - In - One" name="threeInOne" hint="Example: 4,000" />
						<FormField label="Four - In - One" name="fourInOne" hint="Example: 3,500" />
						<FormField label="Other" name="others" hint="Example: 6,500" />
					</div>

					<SectionTitle>Status</SectionTitle>
					<div className="ad-form-grid">
						<FormField label="Bedspace Status" name="bedSpaceStatus">
							<Field as="select" id="bedSpaceStatus" name="bedSpaceStatus" className="ad-input">
								<option value="">Select Bedspace Status</option>
								<option value="Full">Full</option>
								<option value="Available">Available</option>
								<option value="N/A">N/A</option>
							</Field>
						</FormField>
						<div className="ad-form-field">
							<span className="ad-form-label">Registration</span>
							<div className={`ad-switch${registered ? " is-on" : ""}`}>
								<MyForm.Check
									type="switch"
									id="custom-switch"
									label={registered ? "Registered" : "Not Registered"}
									checked={registered}
									onChange={() => setRegistered(!registered)}
								/>
							</div>
						</div>
					</div>
				</FormikStep>

				{/* step 3 */}
				<FormikStep
					label="Capacity & Facilities"
					icon={<MdBed />}
					validationSchema={Yup.object({
						totalBedspaces: Yup.number("must be a number").nullable(),
						roomsTotal: Yup.number("must be a number").nullable(),
						femaleCapacity: Yup.number("must be a number").nullable(),
						maleCapacity: Yup.number("must be a number").nullable(),
					})}
				>
					<SectionTitle>Capacity</SectionTitle>
					<div className="ad-form-grid ad-form-grid--4">
						<FormField label="Total Rooms" name="roomsTotal" hint="Example: 25">
							<Field id="roomsTotal" type="number" min="0" name="roomsTotal" className="ad-input" />
						</FormField>
						<FormField label="Total Bed Spaces" name="totalBedspaces" hint="Example: 100">
							<Field id="totalBedspaces" type="number" min="0" name="totalBedspaces" className="ad-input" />
						</FormField>
						<FormField label="Males Capacity" name="maleCapacity" hint="Example: 60">
							<Field id="maleCapacity" type="number" min="0" name="maleCapacity" className="ad-input" />
						</FormField>
						<FormField label="Females Capacity" name="femaleCapacity" hint="Example: 40">
							<Field id="femaleCapacity" type="number" min="0" name="femaleCapacity" className="ad-input" />
						</FormField>
					</div>

					<SectionTitle text="Tick a facility and enter how many the residence has. Unticking a facility that is already saved does not remove it.">
						Facilities
					</SectionTitle>
					<FacilityPicker facilityArr={facilityArr} />
				</FormikStep>

				{/* step 4 */}
				<FormikStep label="Photos" icon={<MdPhotoLibrary />}>
					<SectionTitle text="Leave these empty to keep the current photos.">Cover Image</SectionTitle>
					<div className="ad-cover">
						<div className="ad-cover-preview">
							{coverPreview || residence?.coverImage ? (
								<img
									src={coverPreview || `${API}/api/images/${residence.slug}/${residence.coverImage}`}
									alt="Cover"
								/>
							) : (
								<span className="ad-muted">No Cover Image</span>
							)}
							{coverPreview && <span className="ad-badge-new">New</span>}
						</div>
						<div className="ad-cover-actions">
							<label className="ad-btn ad-btn--outline ad-file">
								<MdCloudUpload /> {coverImage ? "Change cover image" : "Upload a cover image"}
								<input
									type="file"
									accept="image/*"
									onChange={(e) => setCoverImage(e.currentTarget.files[0])}
								/>
							</label>
							{coverImage && (
								<button type="button" className="ad-btn ad-btn--ghost" onClick={() => setCoverImage(undefined)}>
									<MdClose /> Keep current cover
								</button>
							)}
							{coverImage && <span className="ad-hint">{coverImage.name}</span>}
						</div>
					</div>

					<SectionTitle>Gallery Images</SectionTitle>
					{existingImages.length > 0 && (
						<div className="ad-thumbs">
							{existingImages.map((file) => (
								<img key={file} src={`${API}/api/images/${residence.slug}/${file}`} alt="" />
							))}
						</div>
					)}
					<p className="ad-note">
						<MdInfoOutline /> Uploading new gallery images replaces the current gallery.
					</p>
					<div {...getRootProps({ className: `ad-dropzone${isDragActive ? " is-active" : ""}` })}>
						<input {...getInputProps()} />
						<MdCloudUpload className="ad-dropzone-icon" />
						<p>
							{isDragActive ? (
								"Drop the images here"
							) : (
								<>
									<b>Click to choose images</b> or drag and drop them here
								</>
							)}
						</p>
					</div>
					{previews.length > 0 && (
						<>
							<div className="ad-thumbs ad-thumbs--new">
								{previews.map((src, i) => (
									<img key={src} src={src} alt={accepted[i]?.name} />
								))}
							</div>
							<button type="button" className="ad-btn ad-btn--ghost" onClick={() => setAccepted([])}>
								<MdClose /> Clear new images
							</button>
						</>
					)}
				</FormikStep>
			</FormStepper>
		</div>
	);
}

// checkbox + count for every facility, bound to facilities[i]
function FacilityPicker({ facilityArr }) {
	const { values } = useFormikContext();
	if (!facilityArr?.length) return <p className="ad-muted">No facilities have been set up yet.</p>;
	return (
		<div className="ad-facility-grid">
			{facilityArr.map((facility, i) => {
				const id = values.facilities?.[i]?.id;
				const checked = Array.isArray(id) && id.includes(facility._id);
				return (
					<div key={facility._id} className={`ad-facility-pick${checked ? " is-checked" : ""}`}>
						<label className="ad-facility-check">
							<Field name={`facilities[${i}].id`} type="checkbox" value={facility._id} />
							<span>{facility.name}</span>
						</label>
						<Field
							type="number"
							min="0"
							className="ad-input ad-input--sm"
							name={`facilities[${i}].num`}
							placeholder="Qty"
							aria-label={`${facility.name} count`}
							disabled={!checked}
						/>
					</div>
				);
			})}
		</div>
	);
}

export function FormikStep({ children }) {
	return <>{children}</>;
}

export function FormStepper({ children, ...props }) {
	const childrenArray = React.Children.toArray(children);
	const [step, setStep] = useState(0);
	const [isSubmitting, setSubmit] = useState(false);
	const currentChild = childrenArray[step];
	const isLastPage = step === childrenArray.length - 1;

	const goTo = (s) => {
		setStep(s);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<Formik
			{...props}
			validationSchema={currentChild.props?.validationSchema}
			onSubmit={async (values) => {
				if (isLastPage) {
					setSubmit(true);
					await props.onSubmit(values, setSubmit);
				} else {
					goTo(step + 1);
				}
			}}
		>
			<Form className="ad-card ad-form" noValidate>
				<ol className="ad-steps">
					{childrenArray.map((child, i) => (
						<li
							key={i}
							className={`ad-step${i === step ? " is-current" : ""}${i < step ? " is-done" : ""}`}
						>
							<button
								type="button"
								disabled={i >= step || isSubmitting}
								onClick={() => goTo(i)}
								aria-current={i === step ? "step" : undefined}
							>
								<span className="ad-step-dot">{i < step ? <MdCheck /> : child.props.icon || i + 1}</span>
								<span className="ad-step-text">
									<small>Step {i + 1}</small>
									{child.props.label}
								</span>
							</button>
						</li>
					))}
				</ol>

				<div className="ad-form-body">{currentChild}</div>

				<div className="ad-form-foot">
					{step > 0 ? (
						<button
							type="button"
							disabled={isSubmitting}
							onClick={() => goTo(step - 1)}
							className="ad-btn ad-btn--ghost"
						>
							<MdArrowBack /> Back
						</button>
					) : (
						<span />
					)}
					<button disabled={isSubmitting} type="submit" className="ad-btn ad-btn--primary">
						{isSubmitting ? (
							<>
								<span className="ad-spinner" aria-hidden="true" /> Saving…
							</>
						) : isLastPage ? (
							<>
								<MdSave /> Save changes
							</>
						) : (
							<>
								Next <MdArrowForward />
							</>
						)}
					</button>
				</div>
			</Form>
		</Formik>
	);
}
